import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Form, Input } from "edviron-ui-package";
import Select from "react-select";
import { toast } from "react-toastify";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { CREATE_PLAN, GET_USER } from "../../../Qurries";
import { CustomDropdownIndicator } from "../Settlement/Settlement";

function sanitizeForForm(children: React.ReactNode[]) {
  return React.Children.toArray(children).map((child, idx) => {
    if (!React.isValidElement(child)) return child;
    return React.cloneElement(child as any, {
      add_error: () => {},
      name: (child as any).props?.name ?? `field_${idx}`,
    });
  });
}

const safeOptions = <T,>(o: T[] | undefined | null) =>
  Array.isArray(o) ? o : [];

const safeFind = <T extends { value?: any }>(
  list: T[] | undefined | null,
  predicate: (x: T) => boolean
) => (Array.isArray(list) ? list.find(predicate) : undefined) ?? null;

function ensureOption<T>(opts: T[], incoming: any): T | null {
  if (!incoming) return null;
  const val = Array.isArray(incoming) ? incoming[0] : incoming;
  if (typeof val === "string" || typeof val === "number") {
    return (opts as any).find((o: any) => o.value === val) ?? null;
  }
  if (val && (val.value !== undefined || val.label !== undefined)) {
    return (opts as any).find((o: any) => o.value === val.value) ?? null;
  }
  return null;
}

enum PlanType {
  ON_DEMAND = "ON_DEMAND",
  PERIODIC = "PERIODIC",
}

export const SupportedCurrencies = [
  "AFN",
  "ALL",
  "DZD",
  "AOA",
  "ARS",
  "AMD",
  "AWG",
  "AUD",
  "AZN",
  "BSD",
  "BHD",
  "BDT",
  "BBD",
  "BZD",
  "BMD",
  "BTN",
  "BOB",
  "BAM",
  "BWP",
  "BRL",
  "BND",
  "BGN",
  "BIF",
  "KHR",
  "CAD",
  "CVE",
  "KYD",
  "XAF",
  "XPF",
  "CLP",
  "COP",
  "KMF",
  "CDF",
  "CRC",
  "CZK",
  "DKK",
  "DJF",
  "DOP",
  "XCD",
  "EGP",
  "ERN",
  "SZL",
  "ETB",
  "EUR",
  "FKP",
  "FJD",
  "GMD",
  "GEL",
  "GHS",
  "GIP",
  "GTQ",
  "GNF",
  "GYD",
  "HTG",
  "HNL",
  "HKD",
  "HUF",
  "ISK",
  "INR",
  "IDR",
  "IQD",
  "JMD",
  "JPY",
  "JOD",
  "KZT",
  "KES",
  "KWD",
  "KGS",
  "LAK",
  "LBP",
  "LRD",
  "LYD",
  "MOP",
  "MKD",
  "MGA",
  "MWK",
  "MYR",
  "MVR",
  "MRU",
  "MUR",
  "MXN",
  "MDL",
  "MNT",
  "MAD",
  "MZN",
  "NAD",
  "NPR",
  "ILS",
  "TWD",
  "NZD",
  "NIO",
  "NGN",
  "NOK",
  "PGK",
  "PYG",
  "PEN",
  "PHP",
  "PLN",
  "GBP",
  "QAR",
  "CNY",
  "OMR",
  "RON",
  "RUB",
  "RWF",
  "SHP",
  "WST",
  "SAR",
  "RSD",
  "SCR",
  "SLL",
  "SGD",
  "SBD",
  "SOS",
  "ZAR",
  "KRW",
  "LKR",
  "SRD",
  "SEK",
  "CHF",
  "TJS",
  "TZS",
  "THB",
  "TOP",
  "TTD",
  "TND",
  "TRY",
  "TMT",
  "AED",
  "UGX",
  "UAH",
  "UYU",
  "USD",
  "UZS",
  "VUV",
  "VND",
  "XOF",
  "YER",
  "ZMW",
];

export const PLAN_CURRENCY = SupportedCurrencies.map((currency) => ({
  label: currency,
  value: currency,
}));

const CHARGE_EVERY_OPTIONS = [
  { label: "Day(s)", value: "DAY" },
  { label: "Week(s)", value: "WEEK" },
  { label: "Month(s)", value: "MONTH" },
  { label: "Year(s)", value: "YEAR" },
];

const selectStyles = {
  control: (provided: any) => ({
    ...provided,
    backgroundColor: "#F6F8FA",
    border: "1px solid #1B163B",
    borderRadius: 4,
    minHeight: 20,
    boxShadow: "none",
  }),
  valueContainer: (provided: any) => ({ ...provided, padding: "5px 8px" }),
  input: (provided: any) => ({ ...provided, margin: 0, padding: 0 }),
  indicatorsContainer: (provided: any) => ({ ...provided, paddingRight: 8 }),
};

export default function CreatePlanForm({
  setOpen,
  onCreated,
}: {
  setOpen: (v: boolean) => void;
  onCreated?: () => void;
}) {
  const [createPlan, { loading }] = useMutation(CREATE_PLAN);

  const {
    data: user_data,
  } = useQuery(GET_USER);

  const schoolId = user_data?.getMerchantQuery?.school_id;

  const [planId, setPlanId] = useState("");
  const [planName, setPlanName] = useState("");
  const [planType, setPlanType] = useState<PlanType>(PlanType.ON_DEMAND);
  const [currency, setCurrency] = useState("INR");

  const [maxAmount, setMaxAmount] = useState("");
  const [recurringAmount, setRecurringAmount] = useState("");
  const [maxDebits, setMaxDebits] = useState("");
  const [planNote, setPlanNote] = useState("");

  const [intervals, setIntervals] = useState(1);
  const [intervalType, setIntervalType] = useState("MONTH");

  const inputProps = (nameProp: string, required: boolean) => ({
    name: (
      <span className="flex items-center gap-1">
        {nameProp}
        {required && <span className="text-red-500">*</span>}
      </span>
    ),
    add_error: () => {},
  });

  const handleSubmit = async () => {
    const maxAmountNum = Number(maxAmount);
    const recurringNum = Number(recurringAmount);

    if (!schoolId || !planId || !planName || !maxAmount) {
      toast.error("All required fields are mandatory");
      return;
    }

    if (planId.includes(" ")) {
      toast.error("Plan ID cannot contain spaces");
      return;
    }

    if ((maxAmountNum && !Number.isFinite(maxAmountNum)) || maxAmountNum <= 0) {
      toast.error("Maximum Amount must be a positive number");
      return;
    }

    if (planType === PlanType.PERIODIC) {
      if (!Number.isFinite(recurringNum) || recurringNum <= 0) {
        toast.error("Recurring Amount must be a positive number");
        return;
      }

      if (recurringNum > maxAmountNum) {
        toast.error("Recurring Amount cannot exceed Maximum Amount");
        return;
      }
    }

    try {
      await createPlan({
        variables: {
          school_id: schoolId,
          plan_id: planId,
          plan_name: planName,
          plan_type: planType,
          currency,
          plan_max_amount: parseFloat(maxAmount),
          plan_recurring_amount:
            planType === PlanType.PERIODIC
              ? parseFloat(recurringAmount)
              : undefined,
          plan_max_cycles: parseFloat(maxDebits),
          plan_intervals:
            planType === PlanType.PERIODIC ? intervals : undefined,
          plan_interval_type:
            planType === PlanType.PERIODIC
              ? intervalType || "MONTH"
              : undefined,
          plan_note: planNote,
        },
      });

      toast.success("Plan created successfully");
      onCreated?.();
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.graphQLErrors?.[0]?.message || "Failed to create plan");
    }
  };

  return (
    <div className="flex flex-col bg-white rounded-[4px] h-[80vh] w-full">
      <div className="px-6 py-4 border-b">
        <p className="font-medium">Create Plan</p>
      </div>

      <div className="flex-1 px-6 py-4 overflow-y-auto space-y-4">
        <div className="w-full my-2 "></div>

        <div className="flex justify-between items-center">
          <p className="text-sm font-medium">Subscription Type</p>
          <div className="flex gap-6 text-xs">
            <label>
              <input
                type="radio"
                checked={planType === PlanType.ON_DEMAND}
                onChange={() => setPlanType(PlanType.ON_DEMAND)}
              />{" "}
              On-Demand
            </label>
            <label>
              <input
                type="radio"
                checked={planType === PlanType.PERIODIC}
                onChange={() => setPlanType(PlanType.PERIODIC)}
              />{" "}
              Periodic
            </label>
          </div>
        </div>

        <div className="grid grid-cols-2 ">
          <div className="flex  items-center">
            <p className="text-sm leading-6 font-medium text-gray-900">
              Subscription Currency
              <span className="text-red-500 pl-1">*</span>
            </p>
          </div>
          <Select
            options={safeOptions(PLAN_CURRENCY)}
            value={PLAN_CURRENCY.find((c) => c.value === currency)}
            onChange={(v: any) => setCurrency(v.value)}
            components={{
              DropdownIndicator: CustomDropdownIndicator,
              IndicatorSeparator: () => null,
            }}
            styles={selectStyles}
          />
        </div>

        {planType === PlanType.PERIODIC && (
          <div className="flex gap-2 items-center justify-between">
            <p className="text-sm leading-6 font-medium text-gray-900">
              Charge Customer Once Every
              <span className="text-red-500 pl-1">*</span>
            </p>
            <div className="flex items-center border rounded p-2 gap-1">
              <button onClick={() => setIntervals((v) => Math.max(1, v - 1))}>
                -
              </button>
              <span className="px-2">{intervals}</span>
              <button onClick={() => setIntervals((v) => v + 1)}>+</button>
            </div>

            <Select
              options={safeOptions(CHARGE_EVERY_OPTIONS)}
              value={safeFind(
                CHARGE_EVERY_OPTIONS,
                (o) => o.value === intervalType
              )}
              onChange={(v: any) => {
                const resolved = ensureOption(CHARGE_EVERY_OPTIONS, v);
                if (resolved) {
                  setIntervalType(String(resolved.value));
                }
              }}
              styles={selectStyles}
            />
          </div>
        )}

        <Form>
          {sanitizeForForm(
            [
              <Input
                {...inputProps("Plan ID", true)}
                value={planId}
                onChange={(v: any) => setPlanId(String(v ?? ""))}
              />,

              <Input
                {...inputProps("Plan Name", true)}
                value={planName}
                onChange={(v: any) => setPlanName(String(v ?? ""))}
              />,

              <Input
                {...inputProps("Maximum Amount", true)}
                type="number"
                value={maxAmount}
                onChange={(v: any) => setMaxAmount(String(v ?? ""))}
              />,

              planType === PlanType.PERIODIC ? (
                <Input
                  {...inputProps("Recurring Amount", true)}
                  type="number"
                  value={recurringAmount}
                  onChange={(v: any) => setRecurringAmount(String(v ?? ""))}
                />
              ) : null,

              <Input
                {...inputProps("Maximum Debits", false)}
                type="number"
                value={maxDebits}
                onChange={(v: any) => setMaxDebits(String(v ?? ""))}
              />,
              <Input
                {...inputProps("Plan Note", false)}
                value={planNote}
                onChange={(v: any) => setPlanNote(String(v ?? ""))}
              />,
            ].filter(Boolean)
          )}
        </Form>
      </div>

      <div className="px-6 py-4 border-t flex justify-between">
        <button
          onClick={() => setOpen(false)}
          className="text-[#1E1B59] text-sm"
        >
          Cancel
        </button>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="bg-[#1E1B59] text-white px-6 py-2 rounded-[4px] text-sm flex items-center gap-2"
        >
          {loading && (
            <AiOutlineLoading3Quarters className="animate-spin text-xs" />
          )}
          Create Plan
        </button>
      </div>
    </div>
  );
}
